import { Injectable } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ROLE_PERMISSIONS, MODULE_ROUTES } from '../models/role.model';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  // Cache de verificaciones de permisos para evitar ejecuciones repetidas
  private permissionCache = new Map<string, boolean>();
  private lastUserId: string | number | null = null;

  constructor(private authService: AuthService) {
    // Invalidar cache cuando cambie el usuario (observar el signal)
    // Esto se hace automáticamente porque getUserRoles() ya tiene cache
  }

  /**
   * Verifica si el usuario actual tiene un rol específico
   */
  hasRole(role: string): boolean {
    const userRoles = this.authService.getUserRoles();
    return userRoles.includes(role);
  }

  /**
   * Verifica si el usuario actual tiene alguno de los roles especificados
   */
  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.authService.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  /**
   * Verifica si el usuario tiene acceso a un módulo específico (con cache)
   */
  hasModuleAccess(module: string): boolean {
    const cacheKey = `module:${module}`;
    const currentUser = this.authService.currentUser();
    const userId = currentUser?.id;
    
    // Invalidar cache si cambió el usuario
    if (userId !== this.lastUserId) {
      this.permissionCache.clear();
      this.lastUserId = userId || null;
    }
    
    // Retornar cache si existe
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey)!;
    }
    
    const userRoles = this.authService.getUserRoles();
    const primaryRole = userRoles[0];
    
    if (!primaryRole) {
      this.permissionCache.set(cacheKey, false);
      return false;
    }
    
    // Si es Super Admin, tiene acceso a todo
    if (primaryRole === 'ROLE_SUPER_ADMIN') {
      this.permissionCache.set(cacheKey, true);
      return true;
    }
    
    // Verificar permisos específicos para el rol primario
    const permissions = ROLE_PERMISSIONS[primaryRole];
    const hasAccess = permissions && permissions.some(p => p.module === module);
    
    this.permissionCache.set(cacheKey, hasAccess || false);
    return hasAccess || false;
  }

  /**
   * Verifica si el usuario puede realizar una acción específica en un módulo (con cache)
   */
  canPerformAction(module: string, action: string): boolean {
    const cacheKey = `action:${module}:${action}`;
    const currentUser = this.authService.currentUser();
    const userId = currentUser?.id;
    
    // Invalidar cache si cambió el usuario
    if (userId !== this.lastUserId) {
      this.permissionCache.clear();
      this.lastUserId = userId || null;
    }
    
    // Retornar cache si existe
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey)!;
    }
    
    const userRoles = this.authService.getUserRoles();
    const primaryRole = userRoles[0];
    
    if (!primaryRole) {
      this.permissionCache.set(cacheKey, false);
      return false;
    }
    
    // Si es Super Admin, puede hacer todo
    if (primaryRole === 'ROLE_SUPER_ADMIN') {
      this.permissionCache.set(cacheKey, true);
      return true;
    }
    
    // Verificar permisos específicos para el rol primario
    const permissions = ROLE_PERMISSIONS[primaryRole];
    
    if (permissions) {
      const modulePermission = permissions.find(p => p.module === module);
      
      if (modulePermission && modulePermission.actions.includes(action)) {
        this.permissionCache.set(cacheKey, true);
        return true;
      }
    }
    
    this.permissionCache.set(cacheKey, false);
    return false;
  }

  /**
   * Obtiene los módulos accesibles para el usuario actual
   */
  getAccessibleModules(): string[] {
    const userRoles = this.authService.getUserRoles();
    const accessibleModules = new Set<string>();
    
    // Solo usar el primer rol del array
    const primaryRole = userRoles[0];
    if (primaryRole) {
      const permissions = ROLE_PERMISSIONS[primaryRole];
      if (permissions) {
        permissions.forEach(permission => {
          accessibleModules.add(permission.module);
        });
      }
    }
    
    return Array.from(accessibleModules);
  }

  /**
   * Obtiene las rutas accesibles para el usuario actual
   */
  getAccessibleRoutes(): string[] {
    const accessibleModules = this.getAccessibleModules();
    const accessibleRoutes: string[] = [];
    
    accessibleModules.forEach(module => {
      const routes = MODULE_ROUTES[module];
      if (routes) {
        accessibleRoutes.push(...routes);
      }
    });
    
    return accessibleRoutes;
  }

  /**
   * Verifica si una ruta específica es accesible para el usuario
   */
  isRouteAccessible(route: string): boolean {
    const accessibleRoutes = this.getAccessibleRoutes();
    return accessibleRoutes.some(accessibleRoute => 
      route.startsWith(accessibleRoute)
    );
  }

  /**
   * Obtiene los roles del usuario actual
   */
  getUserRoles(): string[] {
    return this.authService.getUserRoles();
  }

  /**
   * Obtiene el rol principal del usuario (posición 0 del array)
   */
  getPrimaryRole(): string | null {
    const roles = this.getUserRoles();
    return roles.length > 0 ? roles[0] : null;
  }

  /**
   * Verifica si el usuario es Super Admin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('ROLE_SUPER_ADMIN');
  }

  /**
   * Verifica si el usuario es Admin
   */
  isAdmin(): boolean {
    return this.hasRole('ROLE_ADMIN');
  }

  /**
   * Verifica si el usuario es Tesorero
   */
  isTreasurer(): boolean {
    return this.hasRole('ROLE_TESORERO');
  }

  /**
   * Verifica si el rol principal es Super Admin
   */
  isPrimaryRoleSuperAdmin(): boolean {
    const primaryRole = this.getPrimaryRole();
    return primaryRole === 'ROLE_SUPER_ADMIN';
  }

  /**
   * Verifica si el rol principal es Admin
   */
  isPrimaryRoleAdmin(): boolean {
    const primaryRole = this.getPrimaryRole();
    return primaryRole === 'ROLE_ADMIN';
  }

  /**
   * Verifica si el rol principal es Tesorero
   */
  isPrimaryRoleTreasurer(): boolean {
    const primaryRole = this.getPrimaryRole();
    return primaryRole === 'ROLE_TESORERO';
  }

  /**
   * Limpia el cache de permisos (útil cuando el usuario cambia o hace logout)
   */
  clearCache(): void {
    this.permissionCache.clear();
    this.lastUserId = null;
  }
}
